<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHomeDateRange } from '@/composables/home/useHomeDateRange'
import { useAuthBff } from '@/composables/bff/useAuthBff'

const { t, locale } = useI18n({ useScope: 'global' })

const currentTime = ref(new Date())
let intervalId: ReturnType<typeof setInterval> | null = null
const { firstName, lastName } = useAuthBff()
const greeting = computed(() => {
  const hour = currentTime.value.getHours()
  if (locale.value === 'fr') return t('home.greeting.morning')
  if (hour < 12) return t('home.greeting.morning')
  if (hour < 18) return t('home.greeting.afternoon')
  return t('home.greeting.evening')
})

const userName = computed(() => {
  return `${firstName.value} ${lastName.value}`.trim()
})

const dateRange = useHomeDateRange()
const dataSinceDate = computed(() => dateRange.value.formattedString)

onMounted(() => {
  intervalId = globalThis.setInterval(() => {
    currentTime.value = new Date()
  }, 60000)
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<template>
  <div class="relative overflow-hidden bg-primary px-10 py-8 m-10 rounded-2xl">
    <!-- Decorative SVG backgrounds -->
    <div class="bg-logo absolute inset-0 opacity-15" aria-hidden="true" />

    <!-- Banner Content -->
    <div class="relative z-10 flex flex-col h-full text-white">
      <div class="flex items-start justify-between">
        <div>
          <h1 class="typography-display-1">
            {{ greeting }}, <span>{{ userName }}</span>
          </h1>
          <p class="mt-3 typography-body-1">{{ t('home.snapshot_subtitle') }}</p>
        </div>

        <!-- Data since at bottom right -->
        <div class="flex justify-end self-end">
          <span
            class="glass-card inline-flex items-center gap-2 typography-body-1-medium text-neutral-6 px-3 py-2"
          >
            <UiIcon name="hours" variant="outline" :clickable="false" />
            {{ dataSinceDate }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.bg-logo {
  background: url('/img/bg-logo-home.png') no-repeat right center;
  background-size: cover;
}

.glass-card {
  position: relative;
  background: #29308a33;
  backdrop-filter: blur(2px);
  border-radius: 16px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 0 12px rgba(255, 255, 255, 0.05);
}

.glass-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 16px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.5),
    transparent 15%,
    transparent 80%,
    rgba(255, 255, 255, 0.5)
  );
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}
</style>
